import { useState } from 'react'
import { initialsFor } from './PersonAvatar'
import './institutionAvatar.css'

/**
 * An institution's visual identity — its logo when the aggregator supplied
 * one, two neutral initials when it did not. `InstitutionsPanel.tsx`'s own
 * monogram column (M5 Ticket 2, #425 §A3).
 *
 * **No per-institution hue.** Neither `ProviderItem` nor `Account` carries a
 * colour field for an institution to draw from — unlike `MerchantBrief
 * .color`, which a user or Plaid's merchant catalogue actually sets.
 * Inventing one here (a hash-of-name palette, say) would be a THIRD ad hoc
 * colour-identity mechanism landing while the other two — `MerchantAvatar`'s
 * real `color` field, and the hue wheel D14 nominates for people — are still
 * unsettled (issues 290/299), which is exactly what `PersonAvatar.tsx`'s own
 * docblock says that rule exists to prevent. The fill is `--chip`, matching
 * `PersonAvatar` exactly, for the identical reason.
 *
 * **A new primitive, not `MerchantAvatar` generalised.** `MerchantAvatar`'s
 * props are `MerchantBrief`-shaped — `color`, `is_global`, `icon` — and an
 * institution has none of them; forcing them optional would mean inventing
 * defaults for fields this domain carries no data for, in service of
 * merchant-specific features (the global badge, the icon-glyph fallback) an
 * institution has no use for. `PersonAvatar` is the closer precedent: a logo
 * when there is one, initials when there is not.
 *
 * **Two letters, via `initialsFor`** — the derivation `PersonAvatar` already
 * applies to a person's name, reused here rather than re-derived: the first
 * letter of each of the first two words, or the first two letters of a
 * single-word name. Called as `initialsFor(name, null)` — an institution has
 * no email fallback to offer it.
 *
 * `broken`/`trackedUrl` mirror `MerchantAvatar`'s identical fix: reset when
 * the URL changes under an unchanged key, via the adjust-state-during-render
 * pattern rather than a `useEffect` that would paint one stale frame with
 * the old URL's `broken` flag first.
 *
 * **Two sizes, `'row' | 'detail'`, matching `MerchantAvatar`/`PersonAvatar`'s
 * existing split** (`merchantAvatar.css`'s `--row`/`--detail` modifiers are
 * the shape this follows). This docblock used to argue for exactly one size
 * — "a second size with no consumer is exactly the shape #229 names" — and
 * that argument was already false by the time M5 Ticket 3 gave this
 * component a second caller (`AccountCard.tsx`'s identity block) without a
 * second size; M5 Ticket 4 gave it a third (`AccountHero.tsx`'s 40px
 * monogram) that DOES need one, which a route-level CSS override tried to
 * bolt on from outside and could only reach `width`/`height` — never the
 * fallback's `font-size`, since a descendant selector in a stylesheet three
 * layers away has no way to know it needs to scale with the box (architect
 * review, #425 T4 gate, blocking 3). `size` carries all three together, the
 * same fix `MerchantAvatar`'s own `--row`/`--detail` split already applies.
 *
 * **The fallback's ink is `--control-ink`, not a hardcoded `--ink`** — for
 * the identical reason `Money.tsx` reads `--control-ink`/`--control-ink-dim`
 * rather than a literal token for a hero figure: `--control-ink` resolves to
 * plain `--ink` on every OPAQUE surface (`tokens.css:482,913` restate the
 * identical value for `.panel`/`.sunk-well`/`.assistant-panel`), so
 * `InstitutionsPanel`'s row and `AccountCard`'s identity block render the
 * BYTE-IDENTICAL pixel as before this changed — but resolves to `--on-accent`
 * inside a `HeroTile` (`tokens.css:889`), which is what makes `size="detail"`
 * safe to place on `AccountHero`'s lime tile. A literal `--ink` there measured
 * **1.11:1 in dark** (pinned, with the fix, in `compositeContrast.test.tsx`)
 * — the identical failure `Money.tsx`'s own docblock records fixing for the
 * hero balance, one primitive over.
 */
export function InstitutionAvatar({
  name,
  logoUrl,
  size = 'row',
}: {
  name: string
  logoUrl: string | null
  size?: 'row' | 'detail'
}) {
  const [broken, setBroken] = useState(false)
  const [trackedUrl, setTrackedUrl] = useState(logoUrl)
  if (logoUrl !== trackedUrl) {
    setTrackedUrl(logoUrl)
    setBroken(false)
  }

  const showLogo = logoUrl != null && !broken

  return (
    <span className={`institution-avatar institution-avatar--${size}`} data-testid="institution-avatar">
      {showLogo ? (
        <img
          className="institution-avatar__logo"
          src={logoUrl!}
          alt={name}
          onError={() => setBroken(true)}
          // Same discipline `MerchantAvatar` documents: a long institution
          // list must not block on images, and the URL goes to a
          // third-party host (Plaid's CDN) so the Referer header is
          // suppressed rather than leaking this app's own URL.
          loading="lazy"
          decoding="async"
          referrerPolicy="no-referrer"
        />
      ) : (
        // `aria-hidden` — the row already shows the institution's name as
        // its own visible text, so the fallback letters are decorative,
        // same call `MerchantAvatar`/`CategoryChip` make for their own dot.
        <span className="institution-avatar__fallback" aria-hidden="true">
          {initialsFor(name, null)}
        </span>
      )}
    </span>
  )
}
